import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import ContentPage from "@/components/contentPage";
import { calcDateDuration, formatDateRange } from "@/utils";
import { TextTruncate } from "@/components/truncatedText";
import RealAssetDeploymentDetailDataAssetList from "@/components/dataAsset/realAssetDeploymentDetailDataAssetList";
import SealogEventTracker from "@/components/sealogEvent/sealogEventTracker";
import { dataAssetSearchSchema } from "@/schemas/dataAssetSearchSchema";

const deploymentDetailQueryOptions = (api: any, deploymentPublicId: string) =>
  queryOptions({
    queryKey: ["realAssetDeploymentDetail", deploymentPublicId],
    queryFn: () =>
      api.dataAccess.realAssetDeploymentDetail({
        publicId: deploymentPublicId,
      }),
    enabled: !!deploymentPublicId,
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

export const Route = createFileRoute(
  "/realassets/$publicId/$deploymentPublicId/",
)({
  component: RealAssetDeploymentDetail,
  validateSearch: dataAssetSearchSchema,
  loader: ({
    context: { queryClient, api },
    params: { deploymentPublicId },
  }) =>
    queryClient.prefetchQuery(
      deploymentDetailQueryOptions(api, deploymentPublicId),
    ),
});

function RealAssetDeploymentDetail() {
  const { api } = Route.useRouteContext();
  const navigate = useNavigate();
  const { deploymentPublicId } = Route.useParams();
  const search = Route.useSearch();

  // Force deployment filter to match the current route's deploymentPublicId
  const currentFilter = search.realAssetDeploymentPublicIds;
  const needsUpdate = !currentFilter || currentFilter.length !== 1 || currentFilter[0] !== deploymentPublicId;
  useEffect(() => {
    if (deploymentPublicId && needsUpdate) {
      navigate({
        search: ((prev: any) => ({
          ...prev,
          realAssetDeploymentPublicIds: [deploymentPublicId],
        })) as any,
        replace: true,
      });
    }
  }, [deploymentPublicId, navigate, needsUpdate]);

  const realAssetDeploymentQuery = useQuery(
    deploymentDetailQueryOptions(api, deploymentPublicId),
  );

  if (realAssetDeploymentQuery.isPending) return "Loading...";
  if (realAssetDeploymentQuery.error) return "An error occurred";

  const deployment = realAssetDeploymentQuery.data;

  return (
    <ContentPage title={deployment.name}>
      <div className="mx-7">
        {/* Info section: Dates, Duration, Child Deployments, Description */}
        <div className="mb-6 mt-9 grid grid-cols-3 gap-4 border-b pb-4">
          <div>
            <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-blue-100">
              Dates
            </h4>
            <p className="text-lg text-blue-400">
              {formatDateRange(
                deployment.deployment_start_dt,
                deployment.deployment_end_dt
              )}
            </p>
          </div>
          <div>
            <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-blue-100">
              Duration
            </h4>
            <p className="text-lg text-blue-400">
              {calcDateDuration(
                deployment.deployment_start_dt,
                deployment.deployment_end_dt
              )}
            </p>
          </div>
          <div>
            <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-blue-100">
              Child Deployments
            </h4>
            <p className="text-lg text-blue-400">
              {deployment.child_deployments.length}
            </p>
          </div>
        </div>
        <div className="mb-6 border-b pb-4">
          <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-blue-100">
            Description
          </h4>
          <TextTruncate text={deployment.description} />
        </div>

        <SealogEventTracker
          deployment={deployment}
          deploymentPublicId={deploymentPublicId}
        />

        <div className="mt-8">
          <RealAssetDeploymentDetailDataAssetList
            shouldGrabFocus={false}
            deployment={deployment}
          />
        </div>
      </div>
    </ContentPage>
  );
}
